import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Avatar,
  Card,
  CardContent,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Fade,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Laptop as LaptopIcon,
  PhoneAndroid as MobileIcon,
  Tablet as TabletIcon,
  DesktopWindows as DesktopIcon,
  DirectionsCar as VehicleIcon,
  Badge as IdCardIcon,
  Build as ToolsIcon,
  Category as OtherIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { viewCompanyProfileInfoAPICall } from '../../../../API/Settings/Company Settings/companySettingsAPI';
import { viewAllCurrencyUpdated } from '../../../../API/Settings/Currency/CurrencyAPI';

const steps = ['Asset Details', 'Employee Assignment', 'Financial & Review'];

const assetTypes = [
  { value: 'LAPTOP', label: 'Laptop', icon: <LaptopIcon fontSize="large" />, description: 'Portable computers for remote work' },
  { value: 'MOBILE', label: 'Mobile Phone', icon: <MobileIcon fontSize="large" />, description: 'Company issued smartphones' },
  { value: 'DESKTOP', label: 'Desktop', icon: <DesktopIcon fontSize="large" />, description: 'Workstations and PCs' },
  { value: 'TABLET', label: 'Tablet', icon: <TabletIcon fontSize="large" />, description: 'iPads and Android tablets' },
  { value: 'VEHICLE', label: 'Vehicle', icon: <VehicleIcon fontSize="large" />, description: 'Company cars or vans' },
  { value: 'ID_CARD', label: 'ID Card', icon: <IdCardIcon fontSize="large" />, description: 'Access and identification cards' },
  { value: 'TOOLS', label: 'Tools', icon: <ToolsIcon fontSize="large" />, description: 'Equipment and hardware tools' },
  { value: 'OTHER', label: 'Other', icon: <OtherIcon fontSize="large" />, description: 'Miscellaneous assets' }
];

const commonBrands = [
  'Apple', 'Dell', 'HP', 'Lenovo', 'Samsung', 
  'Asus', 'Acer', 'Microsoft', 'Sony', 'LG', 
  'Toyota', 'Honda', 'Tata', 'Bosch', 'Stanley', 
  'Other'
];

const AssetForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [isCustomBrand, setIsCustomBrand] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('₹');

  // Redux state for company settings and currency
  const companyProfileData = useSelector((state) => state.companyProfileSlice.value);
  const currencyListUpdated = useSelector((state) => state.viewUpdatedCurrency.value);

  // Fetch initial data
  useEffect(() => {
    if (!companyProfileData) {
      viewCompanyProfileInfoAPICall();
    }
    if (!currencyListUpdated) {
      viewAllCurrencyUpdated({ status: 0 });
    }
  }, []);

  // Determine currency symbol
  useEffect(() => {
    if (companyProfileData?.profile?.currency && currencyListUpdated) {
      // companyProfileData.profile.currency might be an ID or object depending on how it was populated
      // Based on ProfileInfo.jsx, it seems we need to match it
      const currencyId = typeof companyProfileData.profile.currency === 'object' 
        ? companyProfileData.profile.currency._id 
        : companyProfileData.profile.currency;

      const currencyObj = currencyListUpdated.find(c => c._id === currencyId);
      if (currencyObj?.symbol) {
        setCurrencySymbol(currencyObj.symbol);
      }
    }
  }, [companyProfileData, currencyListUpdated]);

  const [formData, setFormData] = useState({
    employeeId: null,
    assetType: '',
    assetName: '',
    assetCode: '',
    serialNumber: '',
    model: '',
    brand: '',
    purchaseValue: '',
    currentValue: '',
    depreciationRate: '',
    assignedDate: new Date().toISOString().split('T')[0],
    expectedReturnDate: '',
    condition: 'GOOD',
    notes: ''
  });

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get('/employee/getAllEmployees');
        if (Array.isArray(response.data)) {
          setEmployees(response.data);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // Handle input change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'purchaseValue' || field === 'depreciationRate') {
      const purchaseValue = field === 'purchaseValue' ? parseFloat(value) : parseFloat(formData.purchaseValue);
      const depreciationRate = field === 'depreciationRate' ? parseFloat(value) : parseFloat(formData.depreciationRate);
      
      if (purchaseValue && depreciationRate) {
        const currentValue = purchaseValue - (purchaseValue * depreciationRate / 100);
        setFormData(prev => ({ ...prev, currentValue: currentValue.toFixed(2) }));
      } else if (purchaseValue && !depreciationRate) {
        setFormData(prev => ({ ...prev, currentValue: purchaseValue }));
      }
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.assetType) {
        Swal.fire('Required', 'Please select an asset type', 'warning');
        return;
      }
      if (!formData.assetName) {
        Swal.fire('Required', 'Please enter asset name', 'warning');
        return;
      }
    }
    if (activeStep === 1) {
      if (!formData.employeeId) {
        Swal.fire('Required', 'Please select an employee', 'warning');
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        employeeId: formData.employeeId._id,
        purchaseValue: parseFloat(formData.purchaseValue) || 0,
        currentValue: parseFloat(formData.currentValue) || 0,
        depreciationRate: parseFloat(formData.depreciationRate) || 0
      };

      const response = await axios.post('/api/employee-asset/create', payload);
      
      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Asset assigned successfully',
          icon: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          navigate('/userdashboard/staff/hrm/assets');
        });
      }
    } catch (error) {
      console.error('Error creating asset:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to assign asset', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderAssetTypeSelection = () => (
    <Grid container spacing={2}>
      {assetTypes.map((type) => (
        <Grid item xs={12} sm={6} md={3} key={type.value}>
          <Card 
            onClick={() => handleChange('assetType', type.value)}
            sx={{ 
              cursor: 'pointer',
              border: formData.assetType === type.value ? '2px solid #6366f1' : '1px solid #e2e8f0',
              bgcolor: formData.assetType === type.value ? '#eef2ff' : '#fff',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 }
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 2 }}>
              <Box sx={{ color: formData.assetType === type.value ? '#6366f1' : '#64748b' }}>
                {type.icon}
              </Box>
              <Typography variant="subtitle2" fontWeight={600}>{type.label}</Typography>
              {formData.assetType === type.value && <CheckCircleIcon sx={{ color: '#6366f1', fontSize: 16, position: 'absolute', top: 8, right: 8 }} />}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Stepper Header */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Fade in={true} key={activeStep}>
          <Box>
            {/* Step 1: Asset Details */}
            {activeStep === 0 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Select Asset Type</Typography>
                  {renderAssetTypeSelection()}
                </Box>
                
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>Asset Information</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Asset Name"
                        value={formData.assetName}
                        onChange={(e) => handleChange('assetName', e.target.value)}
                        placeholder="e.g. MacBook Pro M2"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Asset Code/Tag"
                        value={formData.assetCode}
                        onChange={(e) => handleChange('assetCode', e.target.value)}
                        placeholder="Auto-generated if empty"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Serial Number"
                        value={formData.serialNumber}
                        onChange={(e) => handleChange('serialNumber', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {isCustomBrand ? (
                        <TextField
                          fullWidth
                          autoFocus
                          label="Specify Brand Name"
                          value={formData.brand}
                          onChange={(e) => handleChange('brand', e.target.value)}
                          placeholder="Enter custom brand name"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => {
                                    setIsCustomBrand(false);
                                    handleChange('brand', '');
                                }} size="small">
                                  <CancelIcon />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      ) : (
                        <Autocomplete
                          options={commonBrands}
                          value={commonBrands.includes(formData.brand) ? formData.brand : null}
                          onChange={(event, newValue) => {
                            if (newValue === 'Other') {
                              setIsCustomBrand(true);
                              handleChange('brand', '');
                            } else {
                              handleChange('brand', newValue || '');
                            }
                          }}
                          renderInput={(params) => (
                            <TextField 
                              {...params} 
                              label="Brand" 
                              placeholder="Select or choose Other" 
                            />
                          )}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Model"
                        value={formData.model}
                        onChange={(e) => handleChange('model', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Condition</Typography>
                      <RadioGroup
                        row
                        value={formData.condition}
                        onChange={(e) => handleChange('condition', e.target.value)}
                      >
                        <FormControlLabel value="EXCELLENT" control={<Radio size="small" />} label="Excellent" />
                        <FormControlLabel value="GOOD" control={<Radio size="small" />} label="Good" />
                        <FormControlLabel value="FAIR" control={<Radio size="small" />} label="Fair" />
                      </RadioGroup>
                    </Grid>
                  </Grid>
                </Paper>
              </Stack>
            )}

            {/* Step 2: Employee Assignment */}
            {activeStep === 1 && (
              <Paper sx={{ p: 4, borderRadius: 3, minHeight: 400 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>Assign to Employee</Typography>
                <Stack spacing={3}>
                  <Autocomplete
                    options={employees}
                    getOptionLabel={(option) => `${option.staff_name} (${option.emp_id})`}
                    value={formData.employeeId}
                    onChange={(e, newValue) => handleChange('employeeId', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Employee"
                        placeholder="Start typing name or ID..."
                        variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { py: 1 } }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ py: 2, borderBottom: '1px solid #f1f5f9' }}>
                        <Avatar src={option.imageUrl} sx={{ width: 40, height: 40, mr: 2, bgcolor: '#6366f1' }}>
                          {option.staff_name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">{option.staff_name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.emp_id} • {option.department?.departmentName || 'No Dept'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                  
                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Assignment Date"
                        value={formData.assignedDate}
                        onChange={(e) => handleChange('assignedDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Expected Return (Optional)"
                        value={formData.expectedReturnDate}
                        onChange={(e) => handleChange('expectedReturnDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Assignment Notes"
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Any comments about the assignment condition or terms..."
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>
            )}

            {/* Step 3: Financials & Review */}
            {activeStep === 2 && (
              <Stack spacing={3}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>Financial Details</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Purchase Value"
                        value={formData.purchaseValue}
                        onChange={(e) => handleChange('purchaseValue', e.target.value)}
                        InputProps={{ startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>{currencySymbol}</Typography> }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Depreciation %"
                        value={formData.depreciationRate}
                        onChange={(e) => handleChange('depreciationRate', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Current Value"
                        value={formData.currentValue}
                        onChange={(e) => handleChange('currentValue', e.target.value)}
                        InputProps={{ startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>{currencySymbol}</Typography> }}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="primary">Summary Review</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Asset Type</Typography>
                      <Typography variant="subtitle2">{assetTypes.find(t => t.value === formData.assetType)?.label || formData.assetType}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Asset Name</Typography>
                      <Typography variant="subtitle2">{formData.assetName}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                      <Typography variant="subtitle2">{formData.employeeId?.staff_name || 'Not Selected'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Value</Typography>
                      <Typography variant="subtitle2">{currencySymbol}{formData.purchaseValue || 0}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Stack>
            )}

            {/* Navigation Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={{ px: 3 }}
              >
                Back
              </Button>
              <Box>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    startIcon={<SaveIcon />}
                    disabled={loading}
                    sx={{ px: 4, py: 1, borderRadius: 2, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                  >
                    {loading ? 'Creating...' : 'Create Asset'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ px: 4, py: 1, borderRadius: 2, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}
                  >
                    Next Step
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default AssetForm;
